import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { isHoverForInteractiveType, saveNodeType, terminate } from '@react-financial-charts/interactive/lib/utils';
import { EachFibRetracement } from './Wrapper';
import type { EachFibRetracementProps } from './Wrapper/EachFibRetracement';
import type { ChartId, RenderNode } from '../../..';
import type { DragOverrideBase, FibRetracementRenderNode } from '../../../types/commontypes';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '../../../types/commontypes';

/** EachFibRetracement의 onDrag 콜백 페이로드(x1/y1/x2/y2) + uuid */
interface FibRetracementDragOverride extends DragOverrideBase {
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}
import { clampYToChart, ShapeType } from '../../..';
import { toFibRetracementRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { chartOrder } from '@/utils/chartConfig';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

interface FibonacciRetracementProps {
  readonly enabled: boolean;
  readonly width?: number;
  readonly onStart: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  readonly onComplete?: (e: React.MouseEvent, newRetracements: RenderNode[], moreProps: MoreProps) => void;
  readonly onDragComplete: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, interactives: RenderNode[], moreProps: MoreProps) => void;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  hoverText?: Partial<HoverTextConfig>;
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionOpacity?: number;
  readonly currentPositionRadius?: number;
  readonly retracements: RenderNode[];
  readonly hoverId: string | null;
  readonly chartId?: ChartId;
  readonly sharedState?: DrawingSharedState;
  readonly setSharedState?: React.Dispatch<React.SetStateAction<DrawingSharedState>>;
  readonly isAnyShapeEnabled?: boolean;
}

interface FibonacciRetracementState {
  override?: FibRetracementDragOverride;
  retracement?: RenderNode | null;
}

export class FibonacciRetracement extends React.Component<FibonacciRetracementProps, FibonacciRetracementState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!FibonacciRetracement._cachedDefaultProps) {
      FibonacciRetracement._cachedDefaultProps = {
        enabled: true,
        retracements: [],
        onSelect: noop,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        currentPositionStroke: '#1e53e6',
        currentPositionOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 4,
      };
    }
    return FibonacciRetracement._cachedDefaultProps;
  }
  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved: boolean = false;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;
  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: FibonacciRetracementProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType('retracements').bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.state = {};
  }

  public render() {
    const { override } = this.state;
    const {
      currentPositionStroke,
      currentPositionOpacity,
      currentPositionStrokeWidth,
      currentPositionRadius = FibonacciRetracement.defaultProps.currentPositionRadius,
      retracements,
      onDoubleClick,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(ShapeType.FIB_RETRACEMENT) as FibRetracementRenderNode;
    const appearance = toFibRetracementRFCProps(activeNode);

    const { enabled, hoverText } = this.props;
    const overrideUuid = isDefined(override) ? override.uuid : null;

    const hoverTextWidthDefault = {
      ...FibonacciRetracement.defaultProps.hoverText,
      ...hoverText,
    };

    const tempCondition =
      sharedState?.type === ShapeType.FIB_RETRACEMENT &&
      chartId === sharedState?.chartId &&
      sharedState?.endXY &&
      sharedState?.status !== 'completed';

    const startXY = sharedState?.startXY;
    const endXY = sharedState?.endXY;

    const currentRetracement = tempCondition ? (
      <EachFibRetracement
        interactive={false}
        type={appearance.type}
        uuid={''}
        appearance={appearance}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        hoverText={hoverTextWidthDefault as EachFibRetracementProps['hoverText']}
        x1={startXY?.[0] ?? 0}
        x2={endXY?.[0] ?? 0}
        y1={startXY?.[1] ?? 0}
        y2={endXY?.[1] ?? 0}
        sharedState={sharedState ?? {}}
        onDrag={this.handleDrag}
      />
    ) : null;
    return (
      <g>
        {retracements.map((each, idx) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toFibRetracementRFCProps(each as FibRetracementRenderNode);

          if (each.hidden) return null;
          const merged = each.uuid === overrideUuid ? { ...each, ...override } : each;
          const eachFib = merged as typeof each & {
            hoverText?: Partial<HoverTextConfig>;
            x1?: number;
            y1?: number;
            x2?: number;
            y2?: number;
          };
          const eachHoverText = isDefined(eachFib.hoverText)
            ? { ...hoverTextWidthDefault, ...eachFib.hoverText }
            : hoverTextWidthDefault;

          return (
            <EachFibRetracement
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={(each as unknown as { selected?: boolean }).selected}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              {...(eachFib as unknown as Partial<EachFibRetracementProps>)}
              type={eachAppearance.type}
              x1={eachFib.x1 ?? 0}
              y1={eachFib.y1 ?? 0}
              x2={eachFib.x2 ?? 0}
              y2={eachFib.y2 ?? 0}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              hoverText={eachHoverText as EachFibRetracementProps['hoverText']}
              appearance={eachAppearance}
              onDrag={(e, uuid, xy) => this.handleDrag(e, uuid, xy)}
              onDragComplete={this.handleDragComplete}
              onSelect={this.handleSelect}
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onHover={this.handleHover as EachFibRetracementProps['onHover']}
              uuid={each.uuid}
              locked={each.locked ?? false}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState ?? {}}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {currentRetracement}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionOpacity}
          strokeWidth={currentPositionStrokeWidth}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          onMouseDown={this.handleStart as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void}
          // onClick={this.handleEnd}
          onMouseMove={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleDrawRetracement as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void
          }
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { retracements } = this.props;

    const newRetracements = retracements.map((retracements, _idx) => ({
      ...retracements,
      selected: retracements.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newRetracements, moreProps);
    }
  };

  private readonly handleDrag = (_: React.MouseEvent, uuid: string, xy: Record<string, unknown>) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    this.setState({
      override: {
        uuid,
        ...xy,
      },
    });
  };

  private readonly handleDrawRetracement = (_: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { setSharedState, sharedState, chartId, enabled } = this.props;
    if (!sharedState || sharedState.status !== 'started' || !enabled) return;

    const startChartId = sharedState.chartId;
    const pointerChartId = moreProps.currentCharts?.[0];

    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    this.mouseMoved = true;
    const [x] = xyValue;
    let [, y] = xyValue;

    // Clamp Y if pointer is on a different chart
    y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);

    setSharedState?.({
      ...sharedState,
      endXY: [x, y],
    });
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { retracements, hoverId } = this.props;
    const { override } = this.state;
    if (isDefined(override) && override.uuid === hoverId) {
      const { x1, x2, y1, y2 } = override;
      if (!isDefined(x1) || !isDefined(y1) || !isDefined(x2) || !isDefined(y2)) {
        return;
      }
      const { uuid, ...rest } = override;
      const newRetracements = retracements.map(each =>
        each.uuid === uuid
          ? {
              ...each,
              ...rest,
              selected: true,
              updatedAt: Date.now(),
            }
          : {
              ...each,
              selected: false,
            },
      );
      this.setState(
        {
          override: undefined,
        },
        () => {
          const { onDragComplete } = this.props;
          if (onDragComplete !== undefined) {
            onDragComplete(e, newRetracements, moreProps);
          }
        },
      );
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { sharedState, enabled } = this.props;
    if (!enabled) return;
    if (sharedState?.status !== 'started') {
      this.mouseMoved = false;
      const { onStart } = this.props;
      if (onStart !== undefined) {
        onStart(e, xyValue, moreProps);
      }
    } else {
      const shapesChartId = sharedState?.chartId;
      // Only do min/max clamp if sharedState exists
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xyValue[1]);
      this.handleEnd(e, [xyValue[0], yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { retracements, sharedState, enabled } = this.props;
    const chartId = moreProps.chartId;
    if (!enabled) return;
    if (sharedState?.status === 'started' && sharedState?.startXY) {
      const targetChartId = sharedState?.chartId || chartId;
      const shapeUuid = crypto.randomUUID();
      const sharedStartXY = sharedState.startXY;
      const lastProps = getActiveTemplateNode(ShapeType.FIB_RETRACEMENT);
      const defaults = defaultShapePropertyValues[ShapeType.FIB_RETRACEMENT];
      const initial = { ...defaults, ...lastProps };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newRetracements = [
        ...retracements.map(item => ({ ...item, selected: false })),
        {
          ...initial,
          x1: sharedStartXY[0],
          y1: sharedStartXY[1],
          x2: xyValue[0],
          y2: xyValue[1],
          selected: true,
          type: ShapeType.FIB_RETRACEMENT,
          chartId: targetChartId,
          uuid: shapeUuid,
          hidden: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
      ] as unknown as RenderNode[];
      this.setState(
        {
          retracement: newRetracements[newRetracements.length - 1],
        },
        () => {
          const { onComplete } = this.props;
          if (onComplete !== undefined) {
            onComplete(e, newRetracements, moreProps);
          }
        },
      );
    }
  };
}
